import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    TableCellLayout,
    TableColumnDefinition,
    TableColumnSizingOptions,
    ToolbarButton,
    createTableColumn,
    Tooltip,
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import {
    Add24Regular,
    bundleIcon,
    Delete24Filled,
    Delete24Regular,
    Edit24Filled,
    Edit24Regular,
    Dismiss24Regular,
    Checkmark24Regular,
} from '@fluentui/react-icons';
import { useAppDispatch } from '../../../../store/hooks';
import {
    createWelcomeMessages,
    deleteWelcomeMessages,
    loadWelcomeMessages,
    searchWelcomeMessages,
    selectWelcomeMessagesPanel,
    updateWelcomeMessages,
} from '../../../../store/features/welcomeMessages/welcomeMessagesSlice';
import {
    getConnectToTeams,
    selectHelp,
} from '../../../../store/features/help/helpSlice';
import { useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import ConfirmDeletionDialog from '../../components/ConfirmDeletionDialog/ConfirmDeletionDialog';
import CreateOrUpdateMessageDialogAction, {
    WelcomeMessageDialogMode,
} from './components/CreateOrUpdateMessageDialogAction';
import BulkAddAircraftRegistrationsDialog from './components/BulkAddAircraftRegistrationsDialog';
import { selectAppConfigurationSearchQuery } from '../../../../store/features/appConfiguration/appConfigurationSlice';
import ConfigurationTable from '../../components/ConfigurationTable/ConfigurationTable';
import {
    IConfigRow,
    RowAction,
} from '../../components/ConfigurationTable/ConfigurationTable.utils';

interface WelcomeMessageRow extends IConfigRow {
    value: string;
    autoConnectionEnabled: boolean;
    autoConnectionMessage: string;
    autoConnectionGroundTeamRowKey: string;
    selectedGroundTeamLabel: string;
}

const EditIconBundle = bundleIcon(Edit24Filled, Edit24Regular);
const DeleteIconBundle = bundleIcon(Delete24Filled, Delete24Regular);

const WelcomeMessagesPanel = () => {
    const { t } = useTranslation();
    const dataGridRef = useRef<HTMLDivElement | null>(null);

    const dispatch = useAppDispatch();
    const { items, loadStatus, nextPageToken, pagination } = useSelector(
        selectWelcomeMessagesPanel
    );
    const searchQuery = useSelector(selectAppConfigurationSearchQuery);
    const { connectToTeams, connectToTeamsStatus } = useSelector(selectHelp);

    const [open, setOpen] = useState(false);
    const [flightMessageDialogMode, setFlightMessageDialogMode] =
        useState<WelcomeMessageDialogMode>();
    const [selectedFlight, setSelectedFlight] = useState<string>('');
    const [selectedFlightWelcomeMessage, setSelectedFlightWelcomeMessage] =
        useState<string>('');
    const [selectedAutoConnectionEnabled, setSelectedAutoConnectionEnabled] =
        useState(false);
    const [selectedAutoConnectionMessage, setSelectedAutoConnectionMessage] =
        useState<string>('');
    const [selectedAutoConnectionGroundTeamRowKey, setSelectedAutoConnectionGroundTeamRowKey] =
        useState<string>('');
    const [deletionTarget, setDeletionTarget] = useState<WelcomeMessageRow | null>(null);
    const [bulkAddOpen, setBulkAddOpen] = useState(false);

    const resetDialogState = useCallback((open: boolean) => {
        setOpen(open);
        setSelectedFlight('');
        setSelectedFlightWelcomeMessage('');
        setSelectedAutoConnectionEnabled(false);
        setSelectedAutoConnectionMessage('');
        setSelectedAutoConnectionGroundTeamRowKey('');
    }, []);

    const onScrollCallback = useCallback(
        (ev: Event) => {
            const target = ev.target as HTMLDivElement;
            if (
                nextPageToken &&
                target.scrollTop + target.clientHeight * 6 > target.scrollHeight
            ) {
                dispatch(loadWelcomeMessages());
            }
        },
        [dispatch, nextPageToken]
    );

    const handleScrollDebounced = useDebouncedCallback(onScrollCallback, 400);

    useEffect(() => {
        if (searchQuery) {
            dispatch(searchWelcomeMessages(searchQuery));
        } else {
            dispatch(loadWelcomeMessages());
        }
    }, [dispatch, searchQuery]);

    useEffect(() => {
        dispatch(getConnectToTeams());
    }, [dispatch]);

    useEffect(() => {
        const sectionElement = dataGridRef.current as HTMLDivElement | null;
        if (sectionElement) {
            sectionElement.addEventListener('scroll', handleScrollDebounced);
        }
        return () => {
            if (sectionElement) {
                sectionElement.removeEventListener(
                    'scroll',
                    handleScrollDebounced
                );
            }
        };
    }, [handleScrollDebounced]);

    const onItemEditCallback: RowAction<WelcomeMessageRow> = useCallback(
        (item) => {
            const message = items.find((m) => m.rowKey === item.id);
            if (message) {
                setFlightMessageDialogMode(WelcomeMessageDialogMode.Edit);
                setSelectedFlight(message.rowKey);
                setSelectedFlightWelcomeMessage(message.value);
                setSelectedAutoConnectionEnabled(!!message.autoConnectionEnabled);
                setSelectedAutoConnectionMessage(message.autoConnectionMessage ?? '');
                setSelectedAutoConnectionGroundTeamRowKey(message.autoConnectionGroundTeamRowKey ?? '');
                setOpen(true);
            }
        },
        [items]
    );

    const onDeleteCallback: RowAction<WelcomeMessageRow> = useCallback(
        (item) => {
            setDeletionTarget(item);
        },
        []
    );

    const closeDeletionDialog = useCallback(() => {
        setDeletionTarget(null);
    }, []);

    const openBulkAddDialog = useCallback(() => {
        setBulkAddOpen(true);
    }, []);

    const closeBulkAddDialog = useCallback(() => {
        setBulkAddOpen(false);
    }, []);

    const deletionMessage = useMemo(
        () =>
            deletionTarget
                ? t('ConfirmDeletionDialog_Content').replace(
                    '{flight}',
                    deletionTarget.value
                )
                : '',
        [deletionTarget, t]
    );

    const configRows: WelcomeMessageRow[] = useMemo(
        () =>
            items.map((item) => {
                const selectedGroundTeam = connectToTeams.find(
                    (team) => team.key === item.autoConnectionGroundTeamRowKey
                );

                return {
                    id: item.rowKey,
                    value: item.value,
                    autoConnectionEnabled: !!item.autoConnectionEnabled,
                    autoConnectionMessage: item.autoConnectionMessage ?? '',
                    autoConnectionGroundTeamRowKey: item.autoConnectionGroundTeamRowKey ?? '',
                    selectedGroundTeamLabel: selectedGroundTeam
                        ? `${selectedGroundTeam.teamName} - ${selectedGroundTeam.channelName}`
                        : item.autoConnectionGroundTeamRowKey
                            ? item.autoConnectionGroundTeamRowKey
                            : '—',
                };
            }),
        [connectToTeams, items]
    );

    const welcomeMessagesColumnSizing: TableColumnSizingOptions = {
        flight: {
            minWidth: 40,
            idealWidth: 60,
        },
        proposedMessage: {
            minWidth: 300,
            idealWidth: 330,
        },
        autoConnection: {
            minWidth: 80,
            idealWidth: 110,
        },
        autoConnectionMessage: {
            minWidth: 300,
            idealWidth: 330,
        },
        selectedGroundTeam: {
            minWidth: 220,
            idealWidth: 260,
        },
        actions: {
            minWidth: 80,
            idealWidth: 80,
        },
    };

    const columns = useMemo<TableColumnDefinition<WelcomeMessageRow>[]>(
        () => [
            createTableColumn<WelcomeMessageRow>({
                columnId: 'flight',
                compare: (a, b) => {
                    return (a.id ?? '').localeCompare(b.id ?? '');
                },
                renderHeaderCell: () => {
                    return t('TableHeader_Aircraft');
                },
                renderCell: (item) => {
                    return item.id;
                },
            }),
            createTableColumn<WelcomeMessageRow>({
                columnId: 'proposedMessage',
                compare: (a, b) => {
                    return (a.value ?? '').localeCompare(b.value ?? '');
                },
                renderHeaderCell: () => {
                    return t('TableHeader_ProposedMessage');
                },
                renderCell: (item) => {
                    return item.value;
                },
            }),
            createTableColumn<WelcomeMessageRow>({
                columnId: 'autoConnection',
                compare: (a, b) => {
                    return Number(a.autoConnectionEnabled) - Number(b.autoConnectionEnabled);
                },
                renderHeaderCell: () => {
                    return t('TableHeader_AutoConnect');
                },
                renderCell: (item) => {
                    return item.autoConnectionEnabled ?
                        <Tooltip relationship={"label"} content={t('Common_Enabled')}>
                            <Checkmark24Regular />
                        </Tooltip> :
                        <Tooltip relationship={"label"} content={t('Common_Disabled')}>
                            <Dismiss24Regular />
                        </Tooltip>;
                },
            }),
            createTableColumn<WelcomeMessageRow>({
                columnId: 'autoConnectionMessage',
                compare: (a, b) => {
                    return (a.autoConnectionMessage ?? '').localeCompare(b.autoConnectionMessage ?? '');
                },
                renderHeaderCell: () => {
                    return t('TableHeader_AutoConnectMessage');
                },
                renderCell: (item) => {
                    return item.autoConnectionMessage || '—';
                },
            }),
            createTableColumn<WelcomeMessageRow>({
                columnId: 'selectedGroundTeam',
                compare: (a, b) => {
                    return (a.selectedGroundTeamLabel ?? '').localeCompare(b.selectedGroundTeamLabel ?? '');
                },
                renderHeaderCell: () => {
                    return t('TableHeader_AutoConnectGroundTeam');
                },
                renderCell: (item) => {
                    return item.selectedGroundTeamLabel;
                },
            }),
            createTableColumn<WelcomeMessageRow>({
                columnId: 'actions',
                renderHeaderCell: () => {
                    return <></>;
                },
                renderCell: (item) => {
                    return (
                        <TableCellLayout>
                            <Tooltip
                                content={t('Tooltip_Edit')}
                                relationship="label"
                            >
                                <Button
                                    appearance="transparent"
                                    icon={<EditIconBundle />}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onItemEditCallback(item);
                                    }}
                                />
                            </Tooltip>
                            <Tooltip
                                content={t('Tooltip_Delete')}
                                relationship="label"
                            >
                                <Button
                                    appearance="transparent"
                                    icon={<DeleteIconBundle />}
                                    onClick={() => {
                                        onDeleteCallback(item);
                                    }}
                                />
                            </Tooltip>
                        </TableCellLayout>
                    );
                },
            }),
        ],
        [t, onItemEditCallback, onDeleteCallback]
    );

    return (
        <>
            <ConfigurationTable<WelcomeMessageRow>
                items={configRows}
                columns={columns}
                columnSizingOptions={welcomeMessagesColumnSizing}
                ariaLabel="welcomeMessagesTable"
                scrollContainerRef={dataGridRef}
                pagination={{
                    currentPage: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    totalCount: pagination.totalCount,
                }}
                bottomActions={
                    <>
                        <ToolbarButton
                            onClick={() => {
                                setFlightMessageDialogMode(
                                    WelcomeMessageDialogMode.New
                                );
                                setOpen(true);
                            }}
                            icon={<Add24Regular />}
                        >
                            {t('ToolbarButton_Add')}
                        </ToolbarButton>
                        <ToolbarButton
                            onClick={openBulkAddDialog}
                            icon={<Add24Regular />}
                        >
                            {t('ToolbarButton_BulkAddAircraftRegistrations')}
                        </ToolbarButton>
                    </>
                }
                footerRow={
                    nextPageToken ? (
                        <Button
                            appearance="subtle"
                            onClick={() => {
                                dispatch(loadWelcomeMessages());
                            }}
                            disabled={loadStatus === 'loading'}
                        >
                            {t('Common_LoadMore')}
                        </Button>
                    ) : undefined
                }
                loadStatus={loadStatus === 'loading' ? 'loading' : 'idle'}
            />

            <BulkAddAircraftRegistrationsDialog
                open={bulkAddOpen}
                onClose={closeBulkAddDialog}
                groundTeams={connectToTeams}
                groundTeamLoadStatus={connectToTeamsStatus}
            />

            <CreateOrUpdateMessageDialogAction
                open={open}
                mode={flightMessageDialogMode ?? WelcomeMessageDialogMode.New}
                flight={selectedFlight}
                welcomeMessage={selectedFlightWelcomeMessage}
                autoConnectionEnabled={selectedAutoConnectionEnabled}
                autoConnectionMessage={selectedAutoConnectionMessage}
                autoConnectionGroundTeamRowKey={selectedAutoConnectionGroundTeamRowKey}
                allGroundTeams={connectToTeams}
                loadStatus={connectToTeamsStatus}
                onOpenChange={(_, data) => {
                    resetDialogState(data.open);
                }}
                onSaveBtnClick={(e, { flight, welcomeMessage, autoConnectionEnabled, autoConnectionMessage, autoConnectionGroundTeamRowKey }) => {
                    resetDialogState(false);
                    if (
                        flightMessageDialogMode ===
                        WelcomeMessageDialogMode.Edit
                    ) {
                        dispatch(
                            updateWelcomeMessages({
                                rowKey: flight,
                                value: welcomeMessage,
                                autoConnectionEnabled,
                                autoConnectionMessage,
                                autoConnectionGroundTeamRowKey,
                            })
                        );
                    } else {
                        dispatch(
                            createWelcomeMessages({
                                rowKey: flight,
                                value: welcomeMessage,
                                autoConnectionEnabled,
                                autoConnectionMessage,
                                autoConnectionGroundTeamRowKey,
                            })
                        );
                    }
                }}
            />

            <ConfirmDeletionDialog
                open={!!deletionTarget}
                message={deletionMessage}
                onConfirm={() => {
                    if (!deletionTarget) {
                        return;
                    }

                    resetDialogState(false);
                    dispatch(deleteWelcomeMessages(deletionTarget.id));
                    closeDeletionDialog();
                }}
                onClose={closeDeletionDialog}
            />
        </>
    );
};

export default WelcomeMessagesPanel;
